import { z } from "zod";
import {
  PERSONAL_INFO_FIELDS,
  RETELL_AMBIENT_SOUNDS,
  RETELL_VOICE_MODELS,
  RETELL_DATA_STORAGE_SETTINGS,
  RETELL_POST_CALL_ANALYSIS_MODELS,
  RETELL_STT_MODES,
  RETELL_STT_PROVIDERS,
  RETELL_VOCAB_SPECIALIZATION,
  RETELL_DENOISING_MODES,
  RETELL_PII_CATEGORIES,
} from "@/isomorphic/constants/agent-consts";
import { DEFAULT_AGENT_LANGUAGE, DEFAULT_LANGUAGES } from "@/isomorphic/constants/languages";
import { MEDICAL_SPECIALTY_KEYS, USAGE_CONTEXT_KEYS, PATIENT_TYPE_KEYS } from "@/isomorphic/constants/medical-data";
import {
  INTERNATIONAL_PHONE_NUMBER_ERROR,
  isValidInternationalPhoneNumber,
  normalizeInternationalPhoneNumber,
} from "@/isomorphic/utils/phone-utils";
import { ClassificationSchema } from "./publication";

// ============================================================================
// PHONE CONFIG SCHEMA
// ============================================================================

export const CallerIdSchema = z.preprocess(
  (value) => {
    if (value === undefined) {
      return undefined;
    }

    if (value === null) {
      return null;
    }

    if (typeof value !== "string") {
      return value;
    }

    const trimmed = value.trim();
    return trimmed.length === 0 ? null : trimmed;
  },
  z
    .union([z.string(), z.null()])
    .refine((value) => value === null || isValidInternationalPhoneNumber(value), {
      message: INTERNATIONAL_PHONE_NUMBER_ERROR,
    })
    .transform((value) => (typeof value === "string" ? normalizeInternationalPhoneNumber(value) : value)),
);

export const PhoneConfigSchema = z
  .object({
    response_engine: z.union([
      z.object({
        type: z.literal("retell-llm"),
        llm_id: z.string(),
        version: z.number().nullable().optional(),
      }),
      z.object({
        type: z.literal("custom-llm"),
        llm_websocket_url: z.string(),
      }),
      z.object({
        type: z.literal("conversation-flow"),
        conversation_flow_id: z.string(),
        version: z.number().nullable().optional(),
      }),
    ]),
    voice_id: z.string(),
    agent_name: z.string().nullable().optional(),
    voice_model: z.enum(RETELL_VOICE_MODELS).nullable().optional(),
    fallback_voice_ids: z.array(z.string()).nullable().optional(),
    voice_temperature: z.number().optional(),
    voice_speed: z.number().optional(),
    volume: z.number().optional(),
    responsiveness: z.number().optional(),
    interruption_sensitivity: z.number().optional(),
    enable_backchannel: z.boolean().optional(),
    backchannel_frequency: z.number().optional(),
    backchannel_words: z.array(z.string()).nullable().optional(),
    reminder_trigger_ms: z.number().optional(),
    reminder_max_count: z.number().optional(),
    ambient_sound: z.enum(RETELL_AMBIENT_SOUNDS).nullable().optional(),
    ambient_sound_volume: z.number().optional(),
    language: z.enum(DEFAULT_LANGUAGES).optional(),
    webhook_url: z.string().nullable().optional(),
    webhook_timeout_ms: z.number().optional(),
    boosted_keywords: z.array(z.string()).nullable().optional(),
    data_storage_setting: z.enum(RETELL_DATA_STORAGE_SETTINGS).optional(),
    normalize_for_speech: z.boolean().optional(),
    end_call_after_silence_ms: z.number().optional(),
    max_call_duration_ms: z.number().optional(),
    voicemail_option: z
      .object({
        action: z.union([
          z.object({ type: z.literal("hangup") }),
          z.object({ type: z.literal("prompt"), text: z.string() }),
          z.object({ type: z.literal("static_text"), text: z.string() }),
          z.object({ type: z.literal("bridge_transfer") }),
        ]),
      })
      .nullable()
      .optional(),
    post_call_analysis_data: z
      .array(
        z.union([
          z.object({
            type: z.literal("string"),
            name: z.string(),
            description: z.string(),
            examples: z.array(z.string()).optional(),
          }),
          z.object({
            type: z.literal("enum"),
            name: z.string(),
            description: z.string(),
            choices: z.array(z.string()),
          }),
          z.object({
            type: z.literal("boolean"),
            name: z.string(),
            description: z.string(),
          }),
          z.object({
            type: z.literal("number"),
            name: z.string(),
            description: z.string(),
          }),
        ]),
      )
      .nullable()
      .optional(),
    post_call_analysis_model: z.enum(RETELL_POST_CALL_ANALYSIS_MODELS).nullable().optional(),
    analysis_successful_prompt: z.string().nullable().optional(),
    analysis_summary_prompt: z.string().nullable().optional(),
    begin_message_delay_ms: z.number().optional(),
    ring_duration_ms: z.number().optional(),
    stt_mode: z.enum(RETELL_STT_MODES).optional(),
    custom_stt_config: z
      .object({
        provider: z.enum(RETELL_STT_PROVIDERS),
        endpointing_ms: z.number(),
      })
      .optional(),
    vocab_specialization: z.enum(RETELL_VOCAB_SPECIALIZATION).optional(),
    allow_user_dtmf: z.boolean().optional(),
    user_dtmf_options: z
      .object({
        digit_limit: z.number().nullable().optional(),
        termination_key: z.string().nullable().optional(),
        timeout_ms: z.number().optional(),
      })
      .nullable()
      .optional(),
    denoising_mode: z.enum(RETELL_DENOISING_MODES).optional(),
    pii_config: z
      .object({
        mode: z.literal("post_call"),
        categories: z.array(z.enum(RETELL_PII_CATEGORIES)),
      })
      .optional(),
    is_public: z.boolean().nullable().optional(),
    callerId: CallerIdSchema,
  })
  .partial();

// ============================================================================
// AUTH CONFIG SCHEMA
// ============================================================================

export const AuthConfigSchema = z.object({
  authenticateWithPinCode: z.boolean().optional(),
  pinCodeLength: z.number().optional(),
  authenticateWithPersonalInfo: z.boolean().optional(),
  personalInfoFields: z.array(z.enum(PERSONAL_INFO_FIELDS)).optional(),
  maxAttempts: z.number().optional(),
  authMessage: z.string().optional(),
  sayTrustPhrase: z.boolean().optional(),
  trustPhrase: z.string().optional(),
  askConsent: z.boolean().optional(),
});

// ============================================================================
// OWNERSHIP SCHEMA
// ============================================================================

export const OwnershipSchema = z.object({
  createdWithAi: z.boolean().nullable().optional(),
  creatorId: z.string().optional(),
  creatorFirstName: z.string().nullable().optional(),
  creatorLastName: z.string().nullable().optional(),
  creatorEmail: z.string().nullable().optional(),
  initialAuthorName: z.string().nullable().optional(),
  initialAuthorId: z.string().nullable().optional(),
});

// ============================================================================
// AGENT STATUS ENUM
// ============================================================================

export const AgentStatusSchema = z.enum(["draft", "released", "deployed", "active", "archived"]);

// ============================================================================
// VOICE AGENT MAPPINGS
// ============================================================================
// Map retell_agent_id -> phase_id
export const VoiceAgentMappingsSchema = z.record(z.string(), z.string());

// ============================================================================
// BASE AGENT SCHEMA (matches backend DbAgentOutput)
// ============================================================================

// To sync with DbAgentOutput in backend/schemas/agents.ts
const BaseAgentSchema = z
  .object({
    // Primary fields
    id: z.string(),

    // Version releases
    agentGroupId: z.string(),
    releaseVersion: z.number().nullable(),
    historyVersion: z.number(),
    publishedSourceAgentId: z.string().nullable().optional(), // Harmonisé avec knowledge_bases: published_source_*

    // Relations
    workspaceId: z.string(),
    knowledgeBaseId: z.string().nullable().optional(),
    creatorId: z.string(),

    // Status
    status: AgentStatusSchema,

    // Timestamps
    createdAt: z.string(),
    updatedAt: z.string(),
    voiceAgentsCreatedAt: z.string().nullable().optional(),
    releasedAt: z.string().nullable().optional(),
    deployedAt: z.string().nullable().optional(),
    activatedAt: z.string().nullable().optional(),
    archivedAt: z.string().nullable().optional(),
    releaseNote: z.string().nullable().optional(),

    // Data fields
    name: z.string(),
    description: z.string().nullable().optional(),
    phoneConfig: PhoneConfigSchema.nullable().optional().or(z.object({}).strict()),
    authConfig: AuthConfigSchema.nullable().optional().or(z.object({}).strict()),
    voiceAgentMappings: VoiceAgentMappingsSchema.nullable().optional(),
    deployedVoiceAgentsConfig: z.array(z.any()).nullable().optional(),
    defaultLanguage: z.enum(DEFAULT_LANGUAGES),
    metadata: z.record(z.string(), z.unknown()).nullable().optional(),
  })
  .merge(OwnershipSchema.pick({ initialAuthorName: true, initialAuthorId: true }))
  .merge(ClassificationSchema);

export const AgentSchema = BaseAgentSchema;

export const AgentListItemSchema = BaseAgentSchema.merge(
  OwnershipSchema.pick({ creatorFirstName: true, creatorLastName: true, creatorEmail: true }),
).extend({
  workflowId: z.string().nullable().optional(),
  workflowName: z.string().nullable().optional(),
  knowledgeBaseName: z.string().nullable().optional(),
  workspaceName: z.string().nullable().optional(),
  createdWithAi: z.boolean().nullable().optional(),
  voiceAgentsOutdated: z.boolean().nullable().optional(),
  phasesCount: z.number().nullable().optional(),
  followUpsCount: z.number().nullable().optional(),
});

export const AgentListSchema = z.array(AgentListItemSchema);

// Deployed version schema (for deployed_versions array in agent groups)
export const DeployedVersionSchema = z.object({
  id: z.string(),
  releaseVersion: z.number(),
  status: AgentStatusSchema,
  releasedAt: z.string(),
  deployedAt: z.string().nullable().optional(),
  activatedAt: z.string().nullable().optional(),
  archivedAt: z.string().nullable().optional(),
  voiceAgentsCreatedAt: z.string().nullable().optional(),
  releaseNote: z.string().nullable().optional(),
});

// Agent group schema (extends AgentListItemSchema with deployment info)
export const AgentGroupItemSchema = AgentListItemSchema.extend({
  latestDeployedVersion: z.number().nullable().optional(),
  latestDeployedAt: z.string().nullable().optional(),
  latestDeployedVoiceAgentsCreatedAt: z.string().nullable().optional(),
  deployedVersions: z.array(DeployedVersionSchema).nullable().optional(),
  latestContentUpdatedAt: z.string().nullable().optional(),
});

export const AgentGroupsSchema = z.array(AgentGroupItemSchema);

// Agent with workflow (for get_agent_with_workflow RPC)
// Note: This is a special case where we need the full workflow structure
// The workflow field will be populated by the transformer
export const AgentWithWorkflowSchema = AgentSchema.extend({
  workflow: z.object({
    id: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    phases: z.array(z.unknown()), // Will be Phase[] after transformation
  }),
});

// ============================================================================
// CREATE/UPDATE PAYLOAD SCHEMAS (camelCase for API)
// ============================================================================

export const CreateAgentBodySchema = z.object({
  workspaceId: z.string().uuid("Invalid workspace ID"),
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  phoneConfig: PhoneConfigSchema.optional(),
  authConfig: AuthConfigSchema.optional(),
  specialty: z.enum(MEDICAL_SPECIALTY_KEYS).optional(),
  usageContext: z.enum(USAGE_CONTEXT_KEYS).optional(),
  operations: z.array(z.string()).optional(),
  patientTypes: z.array(z.enum(PATIENT_TYPE_KEYS)).optional(),
  tags: z.array(z.string()).optional(),
  defaultLanguage: z.enum(DEFAULT_LANGUAGES).default(DEFAULT_AGENT_LANGUAGE),
});

export const UpdateAgentBodySchema = z.object({
  historyVersion: z.number().int().positive(),
  name: z.string().min(1).optional(),
  description: z.string().optional(),
  phoneConfig: PhoneConfigSchema.optional(),
  authConfig: AuthConfigSchema.optional(),
  voiceAgentMappings: VoiceAgentMappingsSchema.optional(),
  specialty: z.enum(MEDICAL_SPECIALTY_KEYS).optional(),
  usageContext: z.enum(USAGE_CONTEXT_KEYS).optional(),
  operations: z.array(z.string()).optional(),
  patientTypes: z.array(z.enum(PATIENT_TYPE_KEYS)).optional(),
  tags: z.array(z.string()).optional(),
  defaultLanguage: z.enum(DEFAULT_LANGUAGES).optional(),
});

// ============================================================================
// TEST AGENT SCHEMAS
// ============================================================================

export const TestPatientDataSchema = z.object({
  // Mode d'authentification
  authMode: z.enum(["none", "default", "custom"]),

  // Informations patient (toujours requis)
  firstName: z.string().min(2, "First name is required"),
  lastName: z.string().min(2, "Last name is required"),
  language: z.enum(DEFAULT_LANGUAGES),

  // Configuration d'authentification (toujours présente)
  enablePinCode: z.boolean(),
  enableYearOfBirth: z.boolean(),
  enableTrustPhrase: z.boolean(),
  enableConsent: z.boolean(),

  // Valeurs d'authentification (conditionnelles)
  pinCode: z.string().optional(),
  yearOfBirth: z.string().optional(),
  trustPhrase: z.string().optional(),
});

export const TestAgentPhoneRequestSchema = z.object({
  phaseId: z.string().uuid("Invalid phase ID"),
  testerPhone: z.string().min(1, "Tester phone is required"),
  testDynamicVariables: TestPatientDataSchema,
  patientAuthData: z
    .object({
      pin_code: z.string().transform((val) => (val ? String(val).trim() : val)),
      year_of_birth: z.string().transform((val) => (val ? String(val).trim() : val)),
    })
    .partial()
    .optional(),
  fromNumber: z.string().optional(), // Backend will determine the from number
});

export const TestAgentWebRequestSchema = z.object({
  phaseId: z.string().uuid("Invalid phase ID"),
  testDynamicVariables: TestPatientDataSchema,
  patientAuthData: z
    .object({
      pin_code: z.string().transform((val) => (val ? String(val).trim() : val)),
      year_of_birth: z.string().transform((val) => (val ? String(val).trim() : val)),
    })
    .partial()
    .optional(),
});

// ============================================================================
// RELEASE AGENT SCHEMA
// ============================================================================

export const ReleaseAgentBodySchema = z.object({
  releaseNote: z.string().optional(),
});

// ============================================================================
// UNDEPLOY VALIDATION SCHEMA
// ============================================================================

export const UndeployValidationResponseSchema = z.object({
  success: z.boolean(),
  agentId: z.string().optional(),
  agentName: z.string().optional(),
  voiceAgentMappings: VoiceAgentMappingsSchema.nullable().optional(),
  message: z.string().optional(),
});

// ============================================================================
// AGENT DETAILS SCHEMA
// ============================================================================

export const AgentDetailsSchema = z.object({
  success: z.boolean(),
  agentId: z.string(),
  workspaceId: z.string(),
  voiceAgentMappings: VoiceAgentMappingsSchema.nullable(),
});

// ============================================================================
// MARK AGENTS RELEASED SCHEMA
// ============================================================================

export const MarkAgentsReleasedResponseSchema = z.array(
  z.object({
    id: z.string(),
    agentName: z.string(),
    previousStatus: z.string(),
  }),
);

export const AgentDefaultFromNumberSchema = z.object({
  callerId: CallerIdSchema,
});

// ============================================================================
// TYPE EXPORTS
// ============================================================================

export type AgentStatus = z.infer<typeof AgentStatusSchema>;
export type PhoneConfig = z.infer<typeof PhoneConfigSchema>;
export type DeployedVersion = z.infer<typeof DeployedVersionSchema>;
export type AgentGroupItem = z.infer<typeof AgentGroupItemSchema>;
export type AuthConfig = z.infer<typeof AuthConfigSchema>;
export type Ownership = z.infer<typeof OwnershipSchema>;
export type VoiceAgentMappings = z.infer<typeof VoiceAgentMappingsSchema>;
export type CreateAgentBody = z.infer<typeof CreateAgentBodySchema>;
export type UpdateAgentBody = z.infer<typeof UpdateAgentBodySchema>;
export type TestPatientData = z.infer<typeof TestPatientDataSchema>;
export type TestAgentPhoneRequest = z.infer<typeof TestAgentPhoneRequestSchema>;
export type TestAgentWebRequest = z.infer<typeof TestAgentWebRequestSchema>;
export type ReleaseAgentBody = z.infer<typeof ReleaseAgentBodySchema>;
export type UndeployValidationResponse = z.infer<typeof UndeployValidationResponseSchema>;
export type AgentDetails = z.infer<typeof AgentDetailsSchema>;
export type MarkAgentsReleasedResponse = z.infer<typeof MarkAgentsReleasedResponseSchema>;
export type AgentDefaultFromNumber = z.infer<typeof AgentDefaultFromNumberSchema>;

// API Response types
export type CreateAgentApiResponse = Agent;
export type UpdateAgentApiResponse = Agent;
export type AgentListApiResponse = z.infer<typeof AgentListSchema>;

export type Agent = z.infer<typeof AgentSchema>;
export type AgentListItem = z.infer<typeof AgentListItemSchema>;
export type AgentWithWorkflow = z.infer<typeof AgentWithWorkflowSchema>;
